import type { DragData } from '../../domain/dnd/types';
import type { EditorSliceState } from '../../domain/redux/types';
import {
  isContainerSelectionOverTarget,
  isDirectBlockDragTarget,
} from '../../domain/selection/targets';
import {
  getLastPointerDownTarget,
  isFrameLabelDragHandle,
  isPointerOnDragItemFrameLabel,
} from './pointer-guard';

function isTextEditingBlock(
  state: EditorSliceState,
  rowId: string,
  columnId: string,
  blockId: string,
): boolean {
  const target = state.textEditingBlock;
  return (
    target !== null &&
    target.rowId === rowId &&
    target.columnId === columnId &&
    target.blockId === blockId
  );
}

function canDragBlock(
  state: EditorSliceState,
  drag: Extract<DragData, { kind: 'item-block' }>,
): boolean {
  if (
    isTextEditingBlock(state, drag.rowId, drag.columnId, drag.blockId)
  ) {
    return false;
  }

  if (
    isPointerOnDragItemFrameLabel(
      getLastPointerDownTarget(),
      'block',
      drag.blockId,
    )
  ) {
    return true;
  }

  if (state.selectedBlockIds.length > 1) {
    return state.selectedBlockIds.includes(drag.blockId);
  }

  const blockTarget = {
    kind: 'block' as const,
    rowId: drag.rowId,
    columnId: drag.columnId,
    blockId: drag.blockId,
  };
  if (
    isContainerSelectionOverTarget(
      state.selection,
      blockTarget,
      state.document.sections,
    )
  ) {
    return false;
  }

  const direct = isDirectBlockDragTarget(
    state.selection,
    drag.rowId,
    drag.columnId,
    drag.blockId,
  );
  if (state.selectedBlockIds.includes(drag.blockId)) {
    return state.primaryBlockId === drag.blockId || direct;
  }

  return direct;
}

function canDragRow(
  state: EditorSliceState,
  drag: Extract<DragData, { kind: 'item-row' }>,
): boolean {
  if (
    isPointerOnDragItemFrameLabel(
      getLastPointerDownTarget(),
      'row',
      drag.rowId,
    )
  ) {
    return true;
  }

  if (state.selectedRowIds.length > 1) {
    return state.selectedRowIds.includes(drag.rowId);
  }

  return state.selection.kind === 'row' && state.selection.rowId === drag.rowId;
}

function canDragGroup(
  state: EditorSliceState,
  drag: Extract<DragData, { kind: 'item-group' }>,
): boolean {
  if (isFrameLabelDragHandle(getLastPointerDownTarget())) {
    return isPointerOnDragItemFrameLabel(
      getLastPointerDownTarget(),
      'group',
      drag.groupId,
    );
  }

  return (
    state.selection.kind === 'group' && state.selection.groupId === drag.groupId
  );
}

export function canDragCanvasItem(
  state: EditorSliceState,
  dragData: DragData,
): boolean {
  if (dragData.kind === 'item-block') {
    return canDragBlock(state, dragData);
  }
  if (dragData.kind === 'item-row') {
    return canDragRow(state, dragData);
  }
  if (dragData.kind === 'item-group') {
    return canDragGroup(state, dragData);
  }
  return false;
}
